import { datadogRum } from '@datadog/browser-rum';
import ReactPixel from 'react-facebook-pixel/src/index';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';

import * as SDK from '@ezbobdev/ui-sdk';

import { checkIsIframe } from '../utils/commons';

// grecaptcha's own promises never settle if the widget's challenge iframe can't complete
// its handshake (e.g. nested cross-origin iframes / restrictive kiosk network) - without this,
// a stuck challenge hangs the caller (and the Continue button) forever with no error and no
// network call ever firing.
const RECAPTCHA_TIMEOUT_MS = 15000;

const withTimeout = (promise, ms, message) => {
  let timeoutId;

  const timeout = new Promise((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

export const ExternalServicesActions = {
  datadog: {
    init() {
      const ddConfig = this.state.actions.settings.getUserConfigProp('externalServices.datadog') ?? {};
      const isAlreadyInitialized = !!datadogRum.getInternalContext();

      if (!ddConfig.enabled || isAlreadyInitialized) return;

      const initOptions = { ...ddConfig.options };
      if (!initOptions.env) {
        initOptions.env = window.location.host;
      }

      datadogRum.init(initOptions);
      datadogRum.startSessionReplayRecording();
    },
    setGlobalContextProperty: datadogRum.setGlobalContextProperty,
    startView: datadogRum.startView,
    addAction: datadogRum.addAction,
    addError: datadogRum.addError,
  },
  googleRecaptcha: {
    async generateTokenVersionThree() {
      const { actions } = this.state;

      const { sitekey } = actions.settings.getUserConfigProp('externalServices.googleRecaptcha') ?? {};

      if (!sitekey) return null;

      SDK.Tools.setOption('recaptcha3Sitekey', sitekey);

      try {
        const token = await withTimeout(
          window.grecaptcha.enterprise.execute(sitekey),
          RECAPTCHA_TIMEOUT_MS,
          'Recaptcha version 3 timed out',
        );

        SDK.Tools.setOption('recaptcha3Token', token);

        return token;
      } catch (error) {
        // Swallowed intentionally - callers don't depend on v3 succeeding.
        actions.externalServices.datadog.addError(error, { source: 'googleRecaptcha.generateTokenVersionThree' });
        console.error(error);
      }
    },
    async generateTokenVersionTwo() {
      const { actions } = this.state;

      const widgetId = SDK.Tools.getOption('recaptcha2WidgetId');

      if (typeof widgetId !== 'number') return null;

      const tokenPromise = new Promise((resolve, reject) => {
        SDK.Tools.setOption('recaptcha2WaiterResolve', resolve);
        SDK.Tools.setOption('recaptcha2WaiterReject', reject);
      });

      window.grecaptcha.enterprise.execute(widgetId).catch((e) => {
        if (String(e) === 'Challenge cancelled by user.') {
          actions.view.togglePopupLoader(false);
        }

        console.error('generateTokenVersionTwo', e);
      });

      try {
        const token = await withTimeout(tokenPromise, RECAPTCHA_TIMEOUT_MS, 'Recaptcha version 2 timed out');

        SDK.Tools.setOption('recaptcha2Token', token);

        return token;
      } catch (error) {
        // Rethrown intentionally - callers already reset the UI on catch.
        actions.externalServices.datadog.addError(error, { source: 'googleRecaptcha.generateTokenVersionTwo' });
        console.error(error);
        throw error;
      }
    },
    resetRecaptchaVersionTwo() {
      SDK.Tools.removeOption('recaptcha2WaiterResolve');
      SDK.Tools.removeOption('recaptcha2WaiterReject');

      const widgetId = SDK.Tools.getOption('recaptcha2WidgetId');

      if (typeof widgetId !== 'number') return;

      window.grecaptcha.enterprise.reset(widgetId);
      SDK.Tools.removeOption('recaptcha2Token');
    },
  },
  googleTagManager: {
    registeredEvents: new Set(),
    init() {
      const { actions } = this.state;
      const { gtmId, enabled, events } = actions.settings.getUserConfigProp(`externalServices.googleTagManager`) ?? {};

      if (!events || !enabled) return;

      if (!gtmId) {
        console.warn('ExternalActions.googleTagManager did not receive GTM ID');
        return;
      }

      const tagManagerArgs = { gtmId };
      // GRN-6623: forward the CSP nonce (set by the server on window.__CSP_NONCE__) so the
      // GTM inline bootstrap is permitted once 'unsafe-inline' is removed from script-src.
      const cspNonce = typeof window !== 'undefined' ? window.__CSP_NONCE__ : undefined;
      if (cspNonce) {
        tagManagerArgs.nonce = cspNonce;
      }
      TagManager.initialize(tagManagerArgs);

      const registeredEvents = actions.externalServices.googleTagManager.registeredEvents;

      const pushToDataLayer = (eventType, payload) => {
        const tagManagerArgs = {
          event: eventType,
          sessionId: SDK.Tools.getOption(`sessionId`),
          userId: SDK.Tools.getOption(`userId`),
          caseId: SDK.Tools.getOption(`caseId`),
          ...payload,
        };

        window.dataLayer.push(tagManagerArgs);
      };

      [...events.view, ...events.interaction].forEach((event) => {
        if (registeredEvents.has(event.type)) return;
        registeredEvents.add(event.type);

        this.state.actions.events.on(event.type, (payload) => {
          const eventType = event.typeMap ?? event.type;
          const { widgetName } = payload;

          if (widgetName) {
            const { category, disableTracking } =
              actions.settings.getUserConfigProp(`widgetsConfig.${widgetName}`) ?? {};

            if (disableTracking) return;

            if (category) {
              payload.category = category;
            }
          }

          pushToDataLayer(eventType, payload);
        });
      });
    },
  },
  googleAnalytics: {
    init() {
      const { actions } = this.state;
      const { measureID, enabled, events, options } =
        actions.settings.getUserConfigProp(`externalServices.googleAnalytics`) ?? {};

      if (!events || !enabled) return;

      if (!measureID) {
        console.warn('ExternalActions.googleAnalytics did not receive measure ID');
        return;
      }

      // GRN-6623: forward the CSP nonce to the gtag script/inline snippet react-ga4 injects.
      const cspNonce = typeof window !== 'undefined' ? window.__CSP_NONCE__ : undefined;
      ReactGA.initialize(measureID, cspNonce ? { ...options, nonce: cspNonce } : options);

      events.view.forEach((event) => {
        this.state.actions.events.on(event, (payload) => {
          const { pageName, stepName, onboardingErrorCode, onboardingErrorName } = payload;
          const page = stepName || pageName;

          if (!page) {
            console.warn('ExternalActions.googleAnalytics received unknown page');
            return;
          }

          // D-02, D-04, EXP-03 (GRN-7081): append the onboarding error code/name onto the
          // existing pageview hit, only when present. react-ga4 forwards unknown keys on
          // send() straight through to the GA4 page_view event as custom parameters (its own
          // gtag wrapper spreads `...rest`), so no parallel event and no direct gtag call is
          // needed. Absent case stays byte-identical to today's { hitType, page } object.
          ReactGA.send({
            hitType: 'pageview',
            page,
            ...(onboardingErrorCode && { onboardingErrorCode, onboardingErrorName }),
          });
        });
      });

      events.interaction.forEach((event) => {
        this.state.actions.events.on(event.type, (payload) => {
          const { widgetName, label } = payload;
          const eventType = event.typeMap ?? event.type;

          const eventPayload = {
            action: eventType,
            category: 'User Interaction',
            caseId: SDK.Tools.getOption(`caseId`),
          };

          if (widgetName) {
            const { category, disableAnalytics } =
              actions.settings.getUserConfigProp(`widgetsConfig.${widgetName}`) ?? {};

            if (disableAnalytics) return;

            eventPayload.widgetName = widgetName;
            eventPayload.category = category ?? eventPayload.category;
          }

          if (label) {
            eventPayload.label = label;
          }

          ReactGA.event(eventType, eventPayload);
        });
      });
    },
  },
  facebookPixel: {
    init() {
      const { actions } = this.state;
      const { pixelID, enabled, autoConfig, debug, events } =
        actions.settings.getUserConfigProp(`externalServices.facebookPixel`) ?? {};

      if (!events || !enabled) return;

      if (!pixelID) {
        console.warn('ExternalActions.facebookPixel did not receive measure ID');
        return;
      }

      const options = {
        autoConfig,
        debug,
      };

      ReactPixel.init(pixelID, null, options);

      events.view.forEach((event) => {
        this.state.actions.events.on(event, (payload) => {
          const { pageName, stepName } = payload;
          const page = stepName || pageName;

          if (!page) {
            console.warn('ExternalActions.facebookPixel received unknown page');
            return;
          }

          if (stepName) {
            ReactPixel.trackCustom('StepView', { page });
          }

          ReactPixel.pageView();
        });
      });

      events.interaction.forEach((event) => {
        this.state.actions.events.on(event.type, (payload) => {
          const { widgetName, label } = payload;

          const eventPayload = {};

          if (widgetName) {
            const { disableAnalytics } = actions.settings.getUserConfigProp(`widgetsConfig.${widgetName}`) ?? {};

            if (disableAnalytics) return;

            eventPayload.widgetName = widgetName;
          }

          if (label) {
            eventPayload.label = label;
          }

          ReactPixel.trackCustom(event.typeMap ?? event.type, eventPayload);
        });
      });
    },
  },
  postMessages: {
    init() {
      const { actions } = this.state;
      const {
        enabled,
        messages = [],
        targetOriginURI = window.location !== window.parent.location ? document.referrer : document.location.href,
      } = actions.settings.getUserConfigProp('externalServices.postMessages') ?? {};

      if (!enabled || typeof window.postMessage !== 'function') return;

      messages.forEach(({ type, typeMap }) => {
        this.state.actions.events.on(type, ({ event, ...payload }) => {
          const messageData = { type: typeMap ?? type, payload };
          window.parent.postMessage(messageData, targetOriginURI);
        });
      });
    },
  },
  iframeMessages: {
    init() {
      const { actions } = this.state;
      const { allowedOrigin, source, messages = [] } = actions.settings.getUserConfigProp('iframe') ?? {};

      if (!allowedOrigin || !messages.length || typeof window.postMessage !== 'function') return;

      if (!checkIsIframe()) return;

      const caseUuid = SDK.Tools.getOption('caseId');
      const externalId = SDK.Tools.getOption('externalId');

      messages.forEach(({ type }) => {
        actions.events.on(type, ({ event, ...payload }) => {
          window.parent.postMessage(
            {
              type,
              source,
              caseUuid,
              externalId,
              ...payload,
            },
            allowedOrigin,
          );
        });
      });
    },
  },
  webViewMessages: {
    init() {
      const { actions } = this.state;
      const { enabled, messages = [] } = actions.settings.getUserConfigProp('externalServices.webViewMessages') || {};
      const { webkit, appJSBridge } = window;

      if (!enabled) return;

      messages.forEach(({ type, typeMap }) => {
        this.state.actions.events.on(type, ({ event, ...payload }) => {
          const messageData = { actionType: typeMap ?? type, payload };

          if (webkit) return webkit.messageHandlers.appJSBridge.postMessage(messageData);
          if (appJSBridge) return appJSBridge.postMessage(JSON.stringify(messageData));
        });
      });
    },
  },
};
